import {NumberInput, TextInput} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {z} from 'zod';

import {defaultTo, isNotNil, not} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish, precisionCalculation, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useBasketItemQuantity} from '../../../hooks/useBasketItemQuantity';
import {useTenant} from '../../../hooks/useTenant';
import {EitherQuantityOrError} from '../../../types/basket/EitherQuantityOrError';
import {MappedBasketItem} from '../../../types/basket/MappedBasketItem';
import {MaterialBasketItem} from '../types/MaterialBasketItem';

interface AfterSalesMaterialBasketItemQuantityWithPriceProps<T extends MaterialBasketItem>
  extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  handlingUnit: string | Nullish;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  hasPendingRequest: boolean;
  isPriceHidden?: boolean;
}

export function AfterSalesMaterialBasketItemQuantityWithPrice<T extends MaterialBasketItem>(
  props: AfterSalesMaterialBasketItemQuantityWithPriceProps<T>
) {
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const {quantity, quantityError, handleQuantityChange} = useBasketItemQuantity({
    itemId: props.item.id,
    currentQuantity: props.item.quantity,
    validationSchema: quantitySchema({
      dispensingUnit: props.item.dispensingUnit ?? 1,
      minValue: props.item.minMaxQuantity?.minQuantity,
      maxValue: props.item.minMaxQuantity?.maxQuantity,
    }),
    onQuantityChange: props.onQuantityChange,
  });

  const dispensingUnit = defaultTo(1, props.item.dispensingUnit);

  const totalPrice = formatCurrency(
    props.item.totalPrice?.withoutVat?.amount ?? 0,
    props.item.totalPrice?.withoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  return (
    <HStack justify="space-between">
      <HStack spacing={2}>
        <Box width={29}>
          <NumberInput
            isDisabled={props.hasPendingRequest || not(props.item.quantityEditingAllowed)}
            isInvalid={isNotNil(quantityError)}
            isStepperVisible
            shouldRoundStepsByDifference
            value={quantity}
            step={dispensingUnit}
            errorMessage={quantityError}
            decimalPlaces={4}
            onChange={handleQuantityChange}
            data-testid={suffixTestId('quantity', props)}
          />
        </Box>
        <Box width={11}>
          <TextInput
            isDisabled
            value={props.handlingUnit ?? DEFAULT_HANDLING_UNIT}
            data-testid={suffixTestId('unit', props)}
          />
        </Box>
      </HStack>

      <Show when={not(props.isPriceHidden)}>
        <VStack align="flex-end">
          <Text size="xxSmall" color="tertiary" align="right">
            {i18n.t('general.labels.sellingPriceWithoutVat')}
          </Text>
          <Text size="small" alternative data-testid={suffixTestId('price', props)}>
            {totalPrice}
          </Text>
        </VStack>
      </Show>
    </HStack>
  );
}

const DEFAULT_HANDLING_UNIT = 'pcs';

type QuantitySchema = {
  dispensingUnit: number;
  minValue: number | Nullish;
  maxValue: number | Nullish;
};

const quantitySchema = (props: QuantitySchema) => {
  let schema = z.number();

  if (isNotNil(props.minValue)) {
    schema = schema.min(props.minValue);
  }

  if (isNotNil(props.maxValue)) {
    schema = schema.max(props.maxValue);
  }

  return schema.refine(
    (value) => precisionCalculation.modulo(value, props.dispensingUnit) === 0,
    `${i18n.t('entity.warehouse.labels.quantityMustBeMultipleOfDispensingUnit')} (${props.dispensingUnit})`
  );
};
